/**
 * ConsentModal component
 *
 * google-sign-in/US-004 (T6): Blocking, non-dismissable, app-wide modal
 * shown whenever the global consent-gate flag (T5's useConsentGateStore) is
 * set. Captures T&C/PDPA acceptance via POST /auth/consent (T4's
 * useConsent) and unblocks on success.
 *
 * AC-004.6: this modal is a UI CONVENIENCE ONLY — the actual enforcement is
 * TS-004's server-side require_consent 403. If a client bypasses this modal
 * and calls a gated endpoint directly, the server still returns 403
 * CONSENT_REQUIRED regardless of anything in this component.
 *
 * Overlay/dialog scaffolding (role=dialog, aria-modal, focus-on-open) is
 * reused from AddFirstChildModal.tsx, MINUS every dismiss affordance — this
 * is a compliance gate, not a convenience dialog. There is no Escape-close
 * handler and no backdrop-click-close. The only ways out are "I Accept" or
 * "Log out".
 *
 * TO-LL-036: on submit failure the modal stays open and shows the error
 * inline — never auto-dismiss on failure.
 * TO-LL-013: no navigate() calls anywhere in this component; it is pure
 * open/close state (Log out is handled by useAuth().logout(), which itself
 * performs a Cognito Hosted-UI redirect, not an in-app navigate()).
 */

import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useConsent } from '@/hooks/useConsent';
import { useConsentGateStore } from '@/store/consentGate';
import { useAuth } from '@/hooks/useAuth';
import { TERMS_URL, PRIVACY_URL, TERMS_VERSION, PRIVACY_VERSION } from '@/constants/tnc';

export function ConsentModal() {
  const consentRequired = useConsentGateStore((s) => s.consentRequired);
  const { submitConsent, isSubmitting, error } = useConsent();
  const { logout } = useAuth();

  const acceptButtonRef = useRef<HTMLButtonElement>(null);

  // Focus the primary action on open (WCAG 2.1 AA — mirrors AddFirstChildModal's
  // focus-on-open pattern).
  useEffect(() => {
    if (consentRequired) {
      const timer = setTimeout(() => {
        acceptButtonRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [consentRequired]);

  // Don't render when not required — zero render cost on every other screen.
  if (!consentRequired) return null;

  const serverErrorMessage = error
    ? error.message || 'Something went wrong. Please try again.'
    : null;

  return (
    // Overlay: NO onClick backdrop-close and NO onKeyDown Escape handler —
    // this is a compliance gate, not a dismissable dialog (AC-004.6).
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 overflow-y-auto py-4"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-modal-title"
        className="bg-white rounded-2xl shadow-lg p-6 md:p-8 w-full max-w-sm mx-4 relative"
      >
        <h2 id="consent-modal-title" className="text-lg md:text-xl font-bold text-gray-900 mb-1">
          Please review our Terms and Privacy Policy
        </h2>
        <p className="text-sm text-gray-500 mb-5">
          Please review and accept our Terms of Service and Privacy Policy to continue using
          Family Tutor.
        </p>

        <div className="space-y-2 mb-5 text-sm">
          <p>
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-tutor-600 underline hover:text-tutor-700"
            >
              Terms of Service ({TERMS_VERSION})
            </a>
          </p>
          <p>
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-tutor-600 underline hover:text-tutor-700"
            >
              Privacy Policy ({PRIVACY_VERSION})
            </a>
          </p>
        </div>

        {/* Inline server error — TO-LL-036: surfaced, never swallowed; modal stays open */}
        {serverErrorMessage && (
          <div
            role="alert"
            className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4"
          >
            {serverErrorMessage}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Button
            ref={acceptButtonRef}
            type="button"
            disabled={isSubmitting}
            onClick={() => submitConsent()}
            className="w-full h-11"
          >
            {isSubmitting ? 'Accepting…' : 'I Accept'}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => {
              void logout();
            }}
            className="w-full h-11"
          >
            Log out instead
          </Button>
        </div>
      </div>
    </div>
  );
}
